// src/components/admin/CollegeManagement.js
import React, { useState, useEffect } from 'react';
import apiService from '../../services/ApiServices';
import {
  Container, Row, Col, Card, Button, Table, Form, Modal,
  Spinner, Alert, Badge, InputGroup
} from 'react-bootstrap';
import {
  FaPlus, FaEdit, FaTrash, FaUniversity, FaSave, FaTimes,
  FaList, FaBuilding
} from 'react-icons/fa';

const CollegeManagement = () => {
  const [colleges, setColleges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState('');

  // Modal & Form states
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentCollegeId, setCurrentCollegeId] = useState(null);

  const [formData, setFormData] = useState({
    college_name: '',
    college_code: ''
  });

  // Fetch all colleges
  const fetchColleges = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.get('/api/college');

      if (response.success && Array.isArray(response.message?.showCollege)) {
        setColleges(response.message.showCollege);
      } else {
        setError('Failed to load colleges');
      }
    } catch (err) {
      setError('Error loading colleges: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchColleges();
  }, []);

  // Form change handler
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Open modal - create or edit
  const openModal = (college = null) => {
    if (college) {
      // Edit mode
      setIsEditMode(true);
      setCurrentCollegeId(college.college_id);
      setFormData({
        college_name: college.college_name || '',
        college_code: college.college_code || ''
      });
    } else {
      // Create mode
      setIsEditMode(false);
      setCurrentCollegeId(null);
      setFormData({
        college_name: '',
        college_code: ''
      });
    }
    setShowModal(true);
  };

  // Save - Create or Update
  const handleSave = async () => {
    if (!formData.college_name.trim()) {
      setError('College name is required');
      return;
    }

    try {
      setError(null);
      setSuccessMsg('');

      const payload = {
        college_name: formData.college_name.trim(),
        college_code: formData.college_code.trim() || null
      };

      let response;
      if (isEditMode) {
        response = await apiService.put(`/api/college/${currentCollegeId}`, payload);
      } else {
        response = await apiService.post('/api/college', payload);
      }

      if (response.success) {
        setSuccessMsg(isEditMode ? 'College updated successfully' : 'College created successfully');
        setShowModal(false);
        fetchColleges();
      } else {
        setError(response.message || 'Operation failed');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Something went wrong');
    }
  };

  // Delete college
  const handleDelete = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete "${name}" college?`)) return;

    try {
      const response = await apiService.delete(`/api/college/${id}`);
      if (response.success) {
        setSuccessMsg('College deleted successfully');
        fetchColleges();
      } else {
        setError('Failed to delete college');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error deleting college');
    }
  };

  return (
    <Container fluid className="py-4">
      <Row className="mb-4 align-items-center">
        <Col>
          <h2 className="mb-0">
            <FaUniversity className="me-2 text-primary" />
            College Management
          </h2>
          <p className="text-muted">Add, edit and manage colleges/institutions</p>
        </Col>
        <Col xs="auto">
          <Button variant="primary" onClick={() => openModal()}>
            <FaPlus className="me-2" /> Add New College
          </Button>
        </Col>
      </Row>

      {/* Messages */}
      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
      {successMsg && <Alert variant="success" dismissible onClose={() => setSuccessMsg('')}>{successMsg}</Alert>}

      {/* Loading / Table */}
      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Loading colleges...</p>
        </div>
      ) : (
        <Card className="border-0 shadow-sm">
          <Card.Body>
            <Table responsive hover className="align-middle">
              <thead className="bg-light">
                <tr>
                  <th>#</th>
                  <th>College Name</th>
                  <th>College Code</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {colleges.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center py-5 text-muted">
                      No colleges found. Add your first college!
                    </td>
                  </tr>
                ) : (
                  colleges.map((college, index) => (
                    <tr key={college.college_id}>
                      <td>{index + 1}</td>
                      <td>
                        <strong>{college.college_name}</strong>
                      </td>
                      <td>
                        {college.college_code ? (
                          <Badge bg="info">{college.college_code}</Badge>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td>
                        {college.is_active ? (
                          <Badge bg="success">Active</Badge>
                        ) : (
                          <Badge bg="secondary">Inactive</Badge>
                        )}
                      </td>
                      <td>
                        {new Date(college.created_at).toLocaleDateString()}
                      </td>
                      <td>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="me-2"
                          onClick={() => openModal(college)}
                        >
                          <FaEdit />
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDelete(college.college_id, college.college_name)}
                        >
                          <FaTrash />
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}

      {/* Create/Edit Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            {isEditMode ? 'Edit College' : 'Add New College'}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form>
            <Form.Group className="mb-4">
              <Form.Label>College Name *</Form.Label>
              <Form.Control
                name="college_name"
                value={formData.college_name}
                onChange={handleChange}
                placeholder="e.g. Indian Institute of Technology"
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>College Code (optional)</Form.Label>
              <InputGroup>
                <InputGroup.Text><FaBuilding /></InputGroup.Text>
                <Form.Control
                  name="college_code"
                  value={formData.college_code}
                  onChange={handleChange}
                  placeholder="e.g. IIT001, JNU045"
                />
              </InputGroup>
              <Form.Text className="text-muted">
                Unique short code for the college (can be left blank)
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            <FaTimes className="me-2" /> Cancel
          </Button>
          <Button variant="primary" onClick={handleSave}>
            <FaSave className="me-2" />
            {isEditMode ? 'Update College' : 'Add College'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default CollegeManagement;